import { invoicesServiceProxy, excelExportServiceProxy } from "../servicePool";
import { apiUrl } from "../_constants/system";
import {
  PagedResultDtoOfInvoceListDto,
  EditClientInvoiceDto,
  UpdateClientInvoiceDto,
  PagedResultDtoOfClientInvoiceListDto,
  InvoiceComparisonResultDto
} from "../service-proxies";
import * as moment from "moment";
import { UpdateInvoicePaidStatusDto } from "./../service-proxies";

export function getInvoices(
  clientUnitId: number,
  filter: string,
  startDate: moment.Moment,
  dueDate: moment.Moment,
  archived: boolean,
  sorting: string,
  skipCount: number,
  maxResultCount: number
): Promise<PagedResultDtoOfInvoceListDto> {
  return invoicesServiceProxy.getInvoices(
    clientUnitId,
    filter,
    startDate,
    dueDate,
    archived,
    sorting,
    skipCount,
    maxResultCount
  );
}

export function getClientInvoices(
  clientUnitId: number,
  filter: string,
  startDate: moment.Moment,
  dueDate: moment.Moment,
  sorting: string,
  skipCount: number,
  maxResultCount: number
): Promise<PagedResultDtoOfClientInvoiceListDto> {
  return invoicesServiceProxy.getClientInvoices(
    clientUnitId,
    filter,
    startDate,
    dueDate,
    false,
    sorting,
    skipCount,
    maxResultCount
  );
}

export function getClientInvoiceForEdit(
  id: number
): Promise<EditClientInvoiceDto> {
  return invoicesServiceProxy.getClientInvoiceForEdit(id);
}

export function getInvoiceForEdit(id: number): Promise<EditClientInvoiceDto> {
  return invoicesServiceProxy.getClientInvoiceForEdit(id);
}

export function getInvoiceDetails(id: number) {
  return invoicesServiceProxy.getInvoiceDetails(id);
}

export function updateInvoicePaidStatus(
  input: UpdateInvoicePaidStatusDto
): Promise<void> {
  return invoicesServiceProxy.updateInvoicePaidStatus(input);
}

export function updateClientInvoice(
  input: UpdateClientInvoiceDto
): Promise<void> {
  return invoicesServiceProxy.updateClientInvoice(input);
}

export function sendClientInvoice(id: number): Promise<void> {
  return invoicesServiceProxy.sendClientInvoice({ id });
}

export function sendInvoices(invoices): Promise<void> {
  return invoicesServiceProxy.sendClientInvoices({ invoices });
}

export function setIsArchived(id: number, archived: boolean) {
  return invoicesServiceProxy.setInvoiceArchive(id, archived);
}

export function compareInvoices(
  clientUnitId?: number,
  startDate?: moment.Moment,
  endDate?: moment.Moment
): Promise<InvoiceComparisonResultDto> {
  return invoicesServiceProxy.compareInvoices({ clientUnitId, startDate, endDate });
}

export function saveComparisonResults(
  clientUnitId: number,
  startDate?: moment.Moment,
  endDate?: moment.Moment,
  pricingErrors?: any[],
  partialShipments?: any[]
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${apiUrl}/api/services/app/Invoice/SaveComparisonResults`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${(invoicesServiceProxy as any)._token}`
    );

    // No timeout - allow long-running operations to complete
    xhr.timeout = 0;

    // SUCCESS handler
    xhr.onload = function() {
      if (xhr.status === 200 || xhr.status === 204) {
        console.log("Comparison results saved successfully");
        resolve();
      } else {
        // Parse error response from backend
        let errorMessage = `Failed to save comparison results (Status: ${xhr.status})`;

        try {
          const errorResponse = JSON.parse(xhr.responseText);
          // ABP Framework returns errors in this format
          if (errorResponse.error) {
            errorMessage = errorResponse.error.message || errorResponse.error.details || errorMessage;
          } else if (errorResponse.message) {
            errorMessage = errorResponse.message;
          }
        } catch (e) {
          // If response isn't JSON, use statusText
          errorMessage = xhr.statusText || errorMessage;
        }

        console.error("Error saving comparison results:", {
          status: xhr.status,
          statusText: xhr.statusText,
          response: xhr.responseText,
          message: errorMessage
        });

        reject(new Error(errorMessage));
      }
    };

    // NETWORK ERROR handler (connection lost, DNS failure, etc.)
    xhr.onerror = function() {
      const errorMessage = "Network error occurred. Please check your connection and try again.";
      console.error("Network error saving comparison results:", {
        readyState: xhr.readyState,
        status: xhr.status
      });
      reject(new Error(errorMessage));
    };

    // ABORT handler (user cancelled or navigation)
    xhr.onabort = function() {
      const errorMessage = "Request was cancelled.";
      console.warn("Comparison results save request was aborted");
      reject(new Error(errorMessage));
    };

    // TIMEOUT handler (shouldn't fire with timeout=0, but good practice)
    xhr.ontimeout = function() {
      const errorMessage = "Request timed out. The operation may still be running on the server.";
      console.error("Request timed out while saving comparison results");
      reject(new Error(errorMessage));
    };

    // Convert camelCase property names to PascalCase for C# backend
    const convertToPascalCase = (obj: any): any => {
      if (!obj || typeof obj !== 'object') return obj;
      if (Array.isArray(obj)) return obj.map(convertToPascalCase);
      
      const converted: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
          converted[pascalKey] = convertToPascalCase(obj[key]);
        }
      }
      return converted;
    };

    const payload = {
      ClientUnitId: clientUnitId,
      StartDate: startDate ? startDate.toISOString() : null,
      EndDate: endDate ? endDate.toISOString() : null,
      PricingErrors: pricingErrors ? pricingErrors.map(convertToPascalCase) : [],
      PartialShipments: partialShipments ? partialShipments.map(convertToPascalCase) : []
    };

    try {
      xhr.send(JSON.stringify(payload));
    } catch (error) {
      console.error("Error sending comparison results request:", error);
      reject(new Error("Failed to send request: " + error.message));
    }
  });
}

export function downloadInvoice(id: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", `${apiUrl}/api/ExcelExport/ExportInvoice?Id=${id}`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${(excelExportServiceProxy as any)._token}`
    );
    xhr.responseType = "blob";

    xhr.onreadystatechange = function(e) {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const blob = xhr.response;

          // extract filename from HTTP headers
          const disposition = xhr.getResponseHeader("Content-Disposition");

          if (!disposition) {
            return reject("Access control error");
          }

          const match = disposition.match(/\sfilename="([^"]+)";/);
          const fileName =
            match && match.length > 1 ? match[1] : "unknown_invoice.pdf";

          // Simulate download
          const a = document.createElement("a");
          a.href = window.URL.createObjectURL(blob);
          a.download = fileName;
          a.dispatchEvent(
            new MouseEvent(`click`, {
              bubbles: true,
              cancelable: true,
              view: window
            })
          );
          // a.click();

          setTimeout(function() {
            a.remove();
            resolve("ok");
          }, 500);

          // resolve('ok');
        } else {
          console.error("Error while downloading an invoice ", xhr.statusText);
          reject(xhr.statusText);
        }
      }
    };

    xhr.send();
  });
}

export function downloadClientInvoice(id: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(
      "GET",
      `${apiUrl}/api/ExcelExport/ExportClientInvoice?Id=${id}`,
      true
    );
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${(excelExportServiceProxy as any)._token}`
    );
    xhr.responseType = "blob";

    xhr.onreadystatechange = function(e) {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const blob = xhr.response;

          // extract filename from HTTP headers
          const disposition = xhr.getResponseHeader("Content-Disposition");

          if (!disposition) {
            return reject("Access control error");
          }

          const match = disposition.match(/\sfilename="([^"]+)";/);
          const fileName =
            match && match.length > 1 ? match[1] : "unknown_invoice.pdf";

          // Simulate download
          const a = document.createElement("a");
          a.href = window.URL.createObjectURL(blob);
          a.download = fileName;
          a.dispatchEvent(
            new MouseEvent(`click`, {
              bubbles: true,
              cancelable: true,
              view: window
            })
          );
          // a.click();

          setTimeout(function() {
            a.remove();
            resolve("ok");
          }, 500);
        } else {
          console.error("Error while downloading an invoice ", xhr.statusText);
          reject(xhr.statusText);
        }
      }
    };

    xhr.send();
  });
}

export function getPartialShipments(
  startDate?: moment.Moment,
  endDate?: moment.Moment,
  clientUnitId?: number,
  vendorId?: number,
  skipCount?: number,
  maxResultCount?: number,
  sorting?: string
): Promise<any> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let url = `${apiUrl}/api/services/app/Invoice/GetPartialShipments?`;
    if (startDate) url += `StartDate=${startDate.toISOString()}&`;
    if (endDate) url += `EndDate=${endDate.toISOString()}&`;
    if (clientUnitId) url += `ClientUnitId=${clientUnitId}&`;
    if (vendorId) url += `VendorId=${vendorId}&`;
    if (skipCount !== undefined) url += `SkipCount=${skipCount}&`;
    if (maxResultCount !== undefined) url += `MaxResultCount=${maxResultCount}&`;
    if (sorting) url += `Sorting=${encodeURIComponent(sorting)}&`;

    xhr.open("GET", url, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${(invoicesServiceProxy as any)._token}`
    );

    xhr.onreadystatechange = function () {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          resolve(data.result || data);
        } else {
          reject(xhr.statusText);
        }
      }
    };

    xhr.send();
  });
}

export function resolvePartialShipment(
  id: number,
  resolutionNotes?: string
): Promise<any> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${apiUrl}/api/services/app/Invoice/ResolvePartialShipment`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${(invoicesServiceProxy as any)._token}`
    );

    xhr.onreadystatechange = function () {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          resolve(data.result || data);
        } else {
          reject(xhr.statusText);
        }
      }
    };

    xhr.send(JSON.stringify({ Id: id, ResolutionNotes: resolutionNotes }));
  });
}

export function getPricingErrors(
  startDate?: moment.Moment,
  endDate?: moment.Moment,
  clientUnitId?: number,
  vendorId?: number,
  skipCount?: number,
  maxResultCount?: number,
  sorting?: string
): Promise<any> {
  return invoicesServiceProxy.getPricingErrors({
    startDate,
    endDate,
    clientUnitId,
    vendorId,
    skipCount,
    maxResultCount,
    sorting,
    isResolved: false  // Only show unresolved pricing errors
  });
}

export function resolvePricingError(
  id: number,
  resolutionNotes?: string
): Promise<any> {
  return invoicesServiceProxy.resolvePricingError({
    id,
    resolutionNotes
  });
}

export function exportPricingErrorsToExcel(
  startDate?: moment.Moment,
  endDate?: moment.Moment,
  clientUnitId?: number,
  vendorId?: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    let url = `${apiUrl}/api/ExcelExport/ExportPricingErrors?`;
    if (startDate) url += `startDate=${startDate.toISOString()}&`;
    if (endDate) url += `endDate=${endDate.toISOString()}&`;
    if (clientUnitId) url += `clientUnitId=${clientUnitId}&`;
    if (vendorId) url += `vendorId=${vendorId}&`;
    
    xhr.open("GET", url, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${(excelExportServiceProxy as any)._token}`
    );
    xhr.responseType = "blob";

    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const blob = xhr.response;
          const disposition = xhr.getResponseHeader("Content-Disposition");

          if (!disposition) {
            return reject("Access control error");
          }

          const match = disposition.match(/\sfilename="([^"]+)";/);
          const fileName =
            match && match.length > 1 ? match[1] : "pricing_errors.xlsx";

          const a = document.createElement("a");
          a.href = window.URL.createObjectURL(blob);
          a.download = fileName;
          a.dispatchEvent(
            new MouseEvent(`click`, {
              bubbles: true,
              cancelable: true,
              view: window
            })
          );

          setTimeout(function() {
            a.remove();
            resolve("ok");
          }, 500);
        } else {
          console.error("Error while downloading pricing errors", xhr.statusText);
          reject(xhr.statusText);
        }
      }
    };

    xhr.send();
  });
}

export function exportPartialShipmentsToExcel(
  startDate?: moment.Moment,
  endDate?: moment.Moment,
  clientUnitId?: number,
  vendorId?: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    let url = `${apiUrl}/api/ExcelExport/ExportPartialShipments?`;
    if (startDate) url += `startDate=${startDate.toISOString()}&`;
    if (endDate) url += `endDate=${endDate.toISOString()}&`;
    if (clientUnitId) url += `clientUnitId=${clientUnitId}&`;
    if (vendorId) url += `vendorId=${vendorId}&`;

    xhr.open("GET", url, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${(excelExportServiceProxy as any)._token}`
    );
    xhr.responseType = "blob";

    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const blob = xhr.response;
          const disposition = xhr.getResponseHeader("Content-Disposition");

          if (!disposition) {
            return reject("Access control error");
          }

          const match = disposition.match(/\sfilename="([^"]+)";/);
          const fileName =
            match && match.length > 1 ? match[1] : "partial_shipments.xlsx";

          const a = document.createElement("a");
          a.href = window.URL.createObjectURL(blob);
          a.download = fileName;
          a.dispatchEvent(
            new MouseEvent(`click`, {
              bubbles: true,
              cancelable: true,
              view: window
            })
          );

          setTimeout(function() {
            a.remove();
            resolve("ok");
          }, 500);
        } else {
          console.error("Error while downloading partial shipments", xhr.statusText);
          reject(xhr.statusText);
        }
      }
    };

    xhr.send();
  });
}
